import { useCallback, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import useAuth from '../auth/useAuth';
import useIdleTimer from '../auth/useIdleTimer';
import { loginRequest } from '../auth/msalConfig';
import { isIdleLocked, setIdleLocked } from '../auth/idleLockState';
import Logo from './Logo';

/**
 * Inactivity lock. After IDLE_TIMEOUT_MS with no mouse/keyboard/touch
 * activity, an opaque overlay hides the whole portal and the user must
 * re-verify with their Microsoft password to continue.
 *
 * Design notes:
 *  - Unlock is a full-page loginRedirect with prompt:'login' — the SAME
 *    redirect flow as the normal sign-in, which is the only flow that
 *    works reliably with Proxemics' GoDaddy-federated M365. The original
 *    popup-based unlock (acquireTokenPopup) could never complete:
 *    Microsoft hands the sign-in off to sso.godaddy.com, and GoDaddy's
 *    SSO page closes the popup itself mid-flow, so MSAL reported
 *    "user_cancelled" no matter what the user typed. Do NOT switch this
 *    back to a popup.
 *  - The trade-off of the redirect: unlocking reloads the portal, so
 *    unsaved form edits don't survive a lock. Accepted (July 2026) —
 *    the popup that would have preserved them simply didn't work.
 *  - The locked flag is mirrored to sessionStorage so refreshing the tab
 *    while locked re-locks instead of bypassing the overlay. It is NOT
 *    cleared before redirecting — AuthProvider clears it only after
 *    Microsoft confirms a successful interactive sign-in, so cancelling
 *    at the sign-in page comes back still locked.
 *  - AuthProvider also handles the "a DIFFERENT user authenticated"
 *    case by signing out entirely rather than resuming this session.
 *  - Mounted inside Layout, so it only runs for signed-in users.
 */
const IDLE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

export default function IdleLock() {
  const { instance } = useMsal();
  const { account, name, email, isDirector, logout } = useAuth();
  const [locked, setLocked] = useState(isIdleLocked);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState('');

  const lock = useCallback(() => {
    setIdleLocked();
    setLocked(true);
    setError('');
  }, []);

  // Auto-logout is OFF for Directors (per Jillian, Aug 2026) — their
  // sessions never idle-lock. Everyone else locks after IDLE_TIMEOUT_MS
  // (10 minutes) of no activity. The hook still runs (hooks can't be
  // called conditionally) but is disabled for Directors, and the overlay
  // is suppressed for them below even if a stale lock flag lingers.
  useIdleTimer({
    timeoutMs: IDLE_TIMEOUT_MS,
    onIdle: lock,
    enabled: !!account && !locked && !isDirector,
  });

  if (!account || !locked || isDirector) return null;

  const unlock = async () => {
    setRedirecting(true);
    setError('');
    try {
      await instance.loginRedirect({
        ...loginRequest,
        prompt: 'login', // force password re-entry, no silent SSO
        loginHint: account.username,
      });
    } catch (err) {
      // loginRedirect only rejects before navigation (e.g. another
      // interaction already in progress) — surface it and stay locked.
      // eslint-disable-next-line no-console
      console.error('[idle-lock] unlock redirect failed', err);
      setRedirecting(false);
      setError('Could not start verification. Please try again.');
    }
  };

  return (
    <div className="idle-lock" role="dialog" aria-modal="true" aria-label="Session locked">
      <div className="idle-lock-card">
        <Logo variant="mark" size={64} />
        <h1>Session Locked</h1>
        <p className="muted">
          You were away for a few minutes, so the portal locked itself to
          protect your account.
        </p>
        <p className="idle-lock-user">
          {name}
          <span className="tiny muted">{email}</span>
        </p>
        <button
          className="btn btn-primary btn-lg"
          onClick={unlock}
          disabled={redirecting}
        >
          {redirecting
            ? 'Taking you to sign-in…'
            : 'Unlock with your password'}
        </button>
        <p className="tiny muted" style={{ margin: '8px 0 0' }}>
          You&apos;ll verify on the Microsoft sign-in page and come right
          back here.
        </p>
        {error && <p className="idle-lock-error">{error}</p>}
        <button className="btn-link tiny" onClick={logout} disabled={redirecting}>
          Sign out instead
        </button>
      </div>
    </div>
  );
}
